import CloseIcon from "@mui/icons-material/Close";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import type { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import SystemBanner from "../../../components/UIToolkit/molecules/SystemBanner";
import { SystemReactTinyPopover } from "../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { FONT_SIZE, MOTION } from "../../../theme/tokens";
import type { PbjDateRange, PbjPredictionJobCoverageGap } from "../../../utils/models";

// How many gap spans to name before collapsing the rest into "+N more". Three is what fits on one
// line at the dialog's width; past that the list stops being scannable and the user should open the
// job's entries rather than read dates off a banner.
const MAX_NAMED_GAPS = 3;
const WARNING_TRIGGER_SIZE = 32;
const WARNING_POPOVER_ENTER_DELAY_MS = 240;
const WARNING_POPOVER_LEAVE_DELAY_MS = MOTION.DURATION.HOVER;
const WARNING_POPOVER_MIN_WIDTH = 260;

type Props = {
	gaps: PbjPredictionJobCoverageGap[];
	/**
	 * `summary` — one line naming the jobs, for the Prediction tab where the banner is an
	 * interruption and the detail lives a click away.
	 * `detailed` — one line PER job with the exact missing spans, for the Manual Hours dialog where
	 * the user has arrived to act on it and needs to know which dates to enter.
	 */
	variant: "summary" | "detailed";
	/** Renders the dismiss affordance. Omit for a banner that has no "ignore" (the dialog's). */
	onDismiss?: () => void;
	dataTestId?: string;
};

const formatDay = (iso: string): string => DateTime.fromISO(iso).toFormat("MMM d");

// A single-day span reads as one date, not "Jul 4 → Jul 4".
const formatRange = (range: PbjDateRange): string =>
	range.start === range.end ? formatDay(range.start) : `${formatDay(range.start)} – ${formatDay(range.end)}`;

const SummaryPopover: FC<{ gaps: PbjPredictionJobCoverageGap[]; onDismiss?: () => void; dataTestId?: string }> = ({ gaps, onDismiss, dataTestId }) => {
	const { t } = useTranslation();
	const summary = t("pbj_model.prediction.missing_jobs.summary", { jobs: gaps.map((gap) => gap.jobDisplayName).join(", ") });

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1.25,
				maxWidth: WARNING_POPOVER_MIN_WIDTH,
				px: 1.5,
				py: 1.25,
				borderRadius: 2,
				bgcolor: "warning.light",
				color: "warning.dark",
			}}
		>
			<Typography component="p" sx={{ m: 0, fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.45 }}>
				{summary}
			</Typography>
			{onDismiss && (
				<SystemButton
					buttonVariant="secondary"
					size="small"
					noFullWidthMobile
					onClick={onDismiss}
					data-testid={`${dataTestId}-dismiss`}
					sx={{ alignSelf: "flex-end", py: 0.5, px: 1.25, fontSize: FONT_SIZE.SECONDARY }}
				>
					{t("pbj_model.prediction.missing_jobs.dismiss")}
				</SystemButton>
			)}
		</Box>
	);
};

/**
 * "Some jobs are missing from the prediction calculation" — the DAT-608 warning.
 *
 * Renders nothing when there are no gaps, so callers can mount it unconditionally rather than each
 * repeating the emptiness check. Copy is owned here (not passed in) because both surfaces are saying
 * the same thing about the same data and the two must not drift apart.
 */
export const MissingJobsWarning: FC<Props> = ({ gaps, variant, onDismiss, dataTestId }) => {
	const { t } = useTranslation();
	if (gaps.length === 0) return null;

	if (variant === "summary") {
		const trigger: ReactElement = (
			<SystemButton
				buttonVariant="borderless"
				iconButton
				leadingIcon={<WarningAmberOutlinedIcon sx={{ fontSize: 20 }} />}
				aria-label={t("pbj_model.prediction.missing_jobs.title")}
				data-testid={dataTestId}
				sx={(theme) => ({
					width: WARNING_TRIGGER_SIZE,
					height: WARNING_TRIGGER_SIZE,
					minWidth: WARNING_TRIGGER_SIZE,
					color: theme.palette.warning.main,
					"&:hover": { color: theme.palette.warning.dark, backgroundColor: theme.palette.warning.light },
				})}
			/>
		);

		return (
			<SystemReactTinyPopover
				enterDelay={WARNING_POPOVER_ENTER_DELAY_MS}
				leaveDelay={WARNING_POPOVER_LEAVE_DELAY_MS}
				positions={["bottom", "top", "left", "right"]}
				align="end"
				contentRootStyle={{ minWidth: WARNING_POPOVER_MIN_WIDTH }}
				content={<SummaryPopover gaps={gaps} onDismiss={onDismiss} dataTestId={dataTestId} />}
				data-testid={`${dataTestId}-popover`}
			>
				{trigger}
			</SystemReactTinyPopover>
		);
	}

	const dismissAction = onDismiss ? (
		<Tooltip title={t("pbj_model.prediction.missing_jobs.dismiss")}>
			<IconButton size="small" onClick={onDismiss} aria-label={t("pbj_model.prediction.missing_jobs.dismiss")} data-testid={`${dataTestId}-dismiss`}>
				<CloseIcon sx={{ fontSize: 16 }} />
			</IconButton>
		</Tooltip>
	) : undefined;

	const description = (
		<Box component="ul" sx={{ m: 0, pl: 2.25, display: "flex", flexDirection: "column", gap: 0.375 }}>
			{gaps.map((gap) => (
				<Typography component="li" key={gap.jobId} sx={{ fontSize: "inherit", lineHeight: 1.45 }} data-testid={`pbj-model-missing-job-${gap.jobName}`}>
					{gap.fullPeriod
						? t("pbj_model.prediction.missing_jobs.gap_full_period", { job: gap.jobDisplayName })
						: t("pbj_model.prediction.missing_jobs.gap_partial", {
								job: gap.jobDisplayName,
								days: gap.missingDays,
								ranges: gap.gaps.slice(0, MAX_NAMED_GAPS).map(formatRange).join(", "),
							})}
					{!gap.fullPeriod &&
						gap.gaps.length > MAX_NAMED_GAPS &&
						` ${t("pbj_model.prediction.missing_jobs.more_ranges", { count: gap.gaps.length - MAX_NAMED_GAPS })}`}
				</Typography>
			))}
		</Box>
	);

	return (
		<SystemBanner
			tone="warning"
			icon={<WarningAmberOutlinedIcon />}
			title={t("pbj_model.prediction.missing_jobs.title")}
			description={description}
			// The detailed variant's description IS a `<ul>`; a list inside the banner's default
			// `<p>` is invalid nesting and the browser would close the paragraph around it.
			descriptionComponent="div"
			action={dismissAction}
			dataTestId={dataTestId}
		/>
	);
};
